import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useElementItemStore } from 'components/WorkspaceEditor/ElementItemStore'
import {
    useWorkspace2Actions,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useProjectContentItemElementsMutation } from 'components/WorkspaceEditor/api/mutations/projectContentItemElements'
import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import { useTrackChangesQuery } from 'components/WorkspaceEditor/components/TrackChangesPanel/api'
import { useTreeMutationsWithTrackChanges } from 'components/WorkspaceEditor/components/TrackChangesPanel/services'
import { useAutoExpandNewArticle } from 'components/WorkspaceEditor/features/createNewContent'
import {
    useNodeMapActions,
    useNodeMapShallowSelector,
} from 'components/WorkspaceEditor/features/keyOperations'
import { useContentItemViewNode } from 'components/WorkspaceEditor/hooks/useContentItemViewNodeService'
import { useTransactionManager } from 'components/WorkspaceEditor/services/transactionManager'
import {
    ContentItemViewNodesType,
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
    SetProjectContentItemElementsResponse,
} from 'components/WorkspaceEditor/types'
import { KeyGenerationSuccessMessage } from 'pages/ProjectsPage/WorkspacePage/components/Common/addUtils'
import { PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback, useRef } from 'react'
import {
    getRootArticleContentItemIds,
    isActiveTabConsistent,
    isStaleResponse,
    processNonSiblingElementsOnContentMutation,
} from '../../../../utils/contentItemViewNodesUtils'
import {
    getSearchableElementId,
    hasElementWithId,
} from '../../../../utils/workspaceEditorUtils'

const { Enter } = PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING

interface UsePlainTextContentMutationProps {
    setUnsavedData?: (value: string) => void
    setShouldTriggerNewContent?: (value: boolean) => void
}

export interface UseUpdateContentFromMutationResponseProps {
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    updateTreeViewNodes: (nodes: Partial<ContentItemViewNodesType>[]) => void
    handleAutoExpandAfterSave: (
        nodes: Partial<ContentItemViewNodesType>[]
    ) => void
    cleanupAutoExpand: () => void
    fetchTrackChangesDataWithApolloClient: () => void
    setSaveOperationInProgress: (inProgress: boolean) => void
    setShouldTriggerNewContent?: (value: boolean) => void
    setPendingContentItemIdForAddContent: (id: string | null) => void
    initialActiveTabRef: React.MutableRefObject<number | null>
}

/**
 * Custom hook to handle plain text content mutation.
 * It provides methods to add new content and manage optimistic updates.
 *
 * @param {UsePlainTextContentMutationProps} props - Props for the hook.
 * @returns {Object} An object containing methods for handling content mutation.
 */
export const usePlainTextContentMutation = ({
    setUnsavedData,
    setShouldTriggerNewContent,
}: UsePlainTextContentMutationProps = {}) => {
    const { onContentViewFetchSuccess } = useProductFetcherUtils({
        overrideContentItemNodes: false,
    })

    const { executeWithOptimistic } = useTransactionManager()
    const { setTextContentElementToAdd } = useWorkspace2Actions()
    const {
        nodeToAdd,
        addNewArticle,
        addNewParagraph,
        contentItemViewNodes,
        previousContentItemViewNodes,
    } = useContentItemViewNode()
    const isNodeToAddArticle = nodeToAdd?.type === 'Article'
    const { addProjectContentItem } = useProjectContentItemElementsMutation()
    const { updateTreeViewNodes } = useTreeMutationsWithTrackChanges()
    const {
        setSaveOperationInProgress,
        markElementAsBeingAdded,
        markElementAsAdded,
        setPendingContentItemIdForAddContent,
    } = useNodeMapActions()
    const { saveOperationKey } = useNodeMapShallowSelector((state) => ({
        saveOperationKey: state.saveOperationKey,
    }))

    // Pre-calculate if this is an Enter operation to avoid runtime comparison
    const isEnterOperation = saveOperationKey === Enter
    // Utility to reset operation states on errors
    const resetOperationStates = useCallback(() => {
        setShouldTriggerNewContent?.(false)
        setSaveOperationInProgress(false)
        setTextContentElementToAdd(null)
    }, [
        setShouldTriggerNewContent,
        setSaveOperationInProgress,
        setTextContentElementToAdd,
    ])

    // Use a ref to always have access to the latest value
    const latestContentItemViewNodesRef = useRef<
        Partial<ContentItemViewNodesType>[] | null
    >(null)

    // Hook for managing auto-expansion of newly created articles
    const {
        prepareForAutoExpand,
        handleAutoExpandAfterSave,
        cleanupAutoExpand,
    } = useAutoExpandNewArticle()

    // Ref to track activeTab when save operation started (for race condition prevention)
    const initialActiveTabRef = useRef<number | null>(null)

    const { fetchTrackChangesDataWithApolloClient } = useTrackChangesQuery()

    // Hook for processing UPDATE mutation responses
    const { processUpdateResponse } = useUpdateContentFromMutationResponse({
        contentItemViewNodes,
        updateTreeViewNodes,
        handleAutoExpandAfterSave,
        cleanupAutoExpand,
        fetchTrackChangesDataWithApolloClient,
        setSaveOperationInProgress,
        setShouldTriggerNewContent,
        setPendingContentItemIdForAddContent,
        initialActiveTabRef,
    })

    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            try {
                // Get currently selected elementId (family) and activeTab from store
                const currentElementId = useElementItemStore.getState()
                    .elementItemValue.elementId
                const currentActiveTab = useWorkspace2Store.getState().activeTab

                // Check if response is stale ignoring optimistic callback when elementIds are not consistent
                if (isStaleResponse(response, currentElementId)) {
                    cleanupAutoExpand()
                    setSaveOperationInProgress(false)

                    return
                }

                // Check if activeTab changed during save operation (tab switch race condition)
                if (
                    !isActiveTabConsistent(
                        initialActiveTabRef.current,
                        currentActiveTab
                    )
                ) {
                    cleanupAutoExpand()
                    fetchTrackChangesDataWithApolloClient()
                    setSaveOperationInProgress(false)

                    return // Ignoring callback - tab switched
                }

                const optimisticTreeViewNodes = await onContentViewFetchSuccess(
                    response,
                    [],
                    true,
                    latestContentItemViewNodesRef.current
                )

                if (
                    optimisticTreeViewNodes &&
                    optimisticTreeViewNodes.length > 0
                ) {
                    // Auto-expand newly added article if applicable
                    handleAutoExpandAfterSave(optimisticTreeViewNodes)

                    updateTreeViewNodes(optimisticTreeViewNodes)
                    setShouldTriggerNewContent?.(isEnterOperation)
                }

                // Always reset save operation flag after callback completes
                setSaveOperationInProgress(false)
            } catch (error) {
                // silently catch only to prevent blocking user flow
                cleanupAutoExpand()
                resetOperationStates()
            }
        },
        onError: () => {
            cleanupAutoExpand()
            resetOperationStates()
        },
    })

    /**
     * Handles content mutation completion for both ADD and UPDATE operations.
     *
     * UPDATE operations (new approach):
     * - Receives mutation response directly to avoid GET call
     * - Uses adapter to transform response to query format
     * - Processes with existing logic (onContentViewFetchSuccess)
     *
     * ADD operations (existing approach):
     * - No mutation response provided
     * - Makes GET call as before
     */
    const handleContentMutationCompleted = useCallback(
        async (
            textElement: TextContentElement,
            isArticle = false,
            shouldSetTextContentElementToAddNull = false,
            blurStartActiveTab?: number,
            mutationResponse?: SetProjectContentItemElementsResponse,
            originalElementsCount: number = 1,
            changedElementsCount: number = 1
        ) => {
            try {
                initialActiveTabRef.current =
                    blurStartActiveTab ??
                    useWorkspace2Store.getState().activeTab

                if (shouldSetTextContentElementToAddNull) {
                    const {
                        updatedContentItem,
                    } = processNonSiblingElementsOnContentMutation(
                        contentItemViewNodes,
                        [textElement.id]
                    )
                    latestContentItemViewNodesRef.current = updatedContentItem
                    setTextContentElementToAdd(null)
                } else {
                    latestContentItemViewNodesRef.current = null
                }

                // UPDATE operations: Use mutation response directly (no GET call)
                if (mutationResponse?.data) {
                    const elementId = useElementItemStore.getState()
                        .elementItemValue.elementId

                    await processUpdateResponse(
                        mutationResponse,
                        elementId,
                        textElement,
                        isEnterOperation,
                        originalElementsCount,
                        changedElementsCount
                    )
                } else {
                    // ADD operations: Continue using GET call
                    let rootArticleContentItemIds: string[] | null = null

                    if (!isArticle) {
                        // Strip the _additional suffix if it exists for text between pills
                        const elementIdForSearch = getSearchableElementId(
                            textElement.id
                        )

                        rootArticleContentItemIds = getRootArticleContentItemIds(
                            contentItemViewNodes,
                            [elementIdForSearch]
                        )
                    }

                    if (initialActiveTabRef.current === null) {
                        initialActiveTabRef.current = useWorkspace2Store.getState().activeTab
                    }

                    await getContentItemViewNodesWithApolloClient(
                        rootArticleContentItemIds
                    )
                }
            } catch (error) {
                // silently catch only to prevent blocking user flow
                cleanupAutoExpand()
                setSaveOperationInProgress(false)
            }
        },
        [
            contentItemViewNodes,
            setTextContentElementToAdd,
            getContentItemViewNodesWithApolloClient,
            processUpdateResponse,
            cleanupAutoExpand,
            setSaveOperationInProgress,
        ]
    )

    const handleAddArticleContentExecuteOptimisticWithFallback = useCallback(
        async (
            fallbackState: string,
            textElement: TextContentElement,
            blurStartActiveTab?: number
        ) => {
            const variables = addNewArticle(fallbackState) || undefined

            if (!variables) {
                throw new Error(`Unsupported Type: ${nodeToAdd?.type}`)
            }

            // Element should already be marked as being added in the blur handler
            // This is a safety check in case the function is called from elsewhere
            markElementAsBeingAdded(textElement.id)

            // Prepare for auto-expansion if adding an article
            prepareForAutoExpand({
                contentItemViewNodes,
                isNodeToAddArticle,
            })

            initialActiveTabRef.current =
                blurStartActiveTab ?? useWorkspace2Store.getState().activeTab
            setSaveOperationInProgress(true)

            executeWithOptimistic({
                fallbackState,
                asyncAction: () => addProjectContentItem({ variables }),
                successCbFn: async () => {
                    await handleContentMutationCompleted(
                        textElement,
                        isNodeToAddArticle,
                        true,
                        blurStartActiveTab
                    )
                    markElementAsAdded(textElement.id)
                    setSaveOperationInProgress(false)
                },
                errorCbFn: () => {
                    markElementAsAdded(textElement.id)
                    updateTreeViewNodes(
                        previousContentItemViewNodes as Partial<
                            ContentItemViewNodesType
                        >[]
                    )
                    cleanupAutoExpand()
                    resetOperationStates()
                },
            })
        },
        [
            handleContentMutationCompleted,
            executeWithOptimistic,
            isNodeToAddArticle,
            updateTreeViewNodes,
            addProjectContentItem,
            addNewArticle,
            resetOperationStates,
            markElementAsBeingAdded,
            markElementAsAdded,
            prepareForAutoExpand,
            cleanupAutoExpand,
            contentItemViewNodes,
        ]
    )

    const handleAddParagraphContentExecuteOptimisticWithFallback = useCallback(
        async (
            fallbackState: string,
            textElement: TextContentElement,
            blurStartActiveTab?: number
        ) => {
            const variables = addNewParagraph(fallbackState) || undefined

            if (!variables) {
                throw new Error(`Unsupported Type: ${nodeToAdd?.type}`)
            }

            // Element should already be marked as being added in the blur handler
            // This is a safety check in case the function is called from elsewhere
            markElementAsBeingAdded(textElement.id)

            initialActiveTabRef.current =
                blurStartActiveTab ?? useWorkspace2Store.getState().activeTab
            setSaveOperationInProgress(true)

            const isKeyedParagraph = fallbackState.includes(':')

            executeWithOptimistic({
                fallbackState,
                asyncAction: () => addProjectContentItem({ variables }),
                successCbFn: async () => {
                    await handleContentMutationCompleted(
                        textElement,
                        isNodeToAddArticle,
                        true,
                        blurStartActiveTab
                    )

                    markElementAsAdded(textElement.id)
                    setSaveOperationInProgress(false)
                },
                errorCbFn: () => {
                    markElementAsAdded(textElement.id)
                    updateTreeViewNodes(
                        previousContentItemViewNodes as Partial<
                            ContentItemViewNodesType
                        >[]
                    )
                    cleanupAutoExpand()
                    resetOperationStates()
                },
                successMessage: isKeyedParagraph
                    ? KeyGenerationSuccessMessage
                    : undefined,
            })
        },
        [
            handleContentMutationCompleted,
            executeWithOptimistic,
            isNodeToAddArticle,
            updateTreeViewNodes,
            addProjectContentItem,
            addNewParagraph,
            resetOperationStates,
            markElementAsBeingAdded,
            markElementAsAdded,
            cleanupAutoExpand,
        ]
    )

    const handleAddContentExecuteOptimisticWithFallback = useCallback(
        async (
            fallbackState: string,
            textElement: TextContentElement,
            blurStartActiveTab?: number
        ) => {
            if (isNodeToAddArticle) {
                await handleAddArticleContentExecuteOptimisticWithFallback(
                    fallbackState,
                    textElement,
                    blurStartActiveTab
                )
            } else if (!isNodeToAddArticle) {
                await handleAddParagraphContentExecuteOptimisticWithFallback(
                    fallbackState,
                    textElement,
                    blurStartActiveTab
                )
            } else {
                throw new Error(`Unsupported Type: ${nodeToAdd?.type}`)
            }
        },
        [
            handleAddArticleContentExecuteOptimisticWithFallback,
            handleAddParagraphContentExecuteOptimisticWithFallback,
            isNodeToAddArticle,
            nodeToAdd?.type,
        ]
    )

    return {
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
    }
}

/**
 * Hook for processing UPDATE mutation responses directly (no GET call).
 * Handles both update-in-place and remove-and-add strategies.
 *
 * Strategy 1: If response node ID matches existing node → update in place
 * Strategy 2: Otherwise, remove old node + add new nodes (handles empty responses)
 */
export const useUpdateContentFromMutationResponse = ({
    contentItemViewNodes,
    updateTreeViewNodes,
    handleAutoExpandAfterSave,
    cleanupAutoExpand,
    fetchTrackChangesDataWithApolloClient,
    setSaveOperationInProgress,
    setShouldTriggerNewContent,
    setPendingContentItemIdForAddContent,
    initialActiveTabRef,
}: UseUpdateContentFromMutationResponseProps) => {
    const processUpdateResponse = useCallback(
        async (
            mutationResponse: SetProjectContentItemElementsResponse,
            elementId: string,
            textElement: TextContentElement,
            isEnterOperation: boolean,
            originalElementsCount: number,
            changedElementsCount: number
        ) => {
            /*
                It's an enhancement to handle contentItemViewNodes updates during save operations,
                specifically addressing the scenario where the paragraph structure changes (e.g., text added or removed)
                while the user is trying to create new content (like pressing Enter to add a new paragraph).
                * 1. User types in a paragraph and presses Enter
                * 2. The text is saved (mutation happens)
                * 3. The Enter handler tries to create a NEW paragraph after the current one
                * 4. BUT - if the save changed the paragraph (text was added/removed during save),
                *    the Enter handler doesn't know WHICH paragraph ID to create content after
                *
                * REAL EXAMPLE:
                * Step 1: User types "Hello World" in paragraph (not yet saved, temporary ID)
                * Step 2: User presses Enter
                * Step 3: Save happens → server assigns REAL ID to paragraph
                * Step 4: Enter handler needs to create new paragraph but the paragraph ID changed!
                *         Should it create after the OLD temporary ID or NEW real ID?
                *
                * THE FIX:
                * We detect when the save operation changes the paragraph content by comparing:
                * - originalElementsCount: How many text elements before save (usually 1)
                * - changedElementsCount: How many text elements after save
                *
                * If counts don't match, the paragraph structure changed during save.
                * We track the NEW paragraph ID so Enter handler knows where to create next paragraph.
                *
                * WHY "wasContentDeleted"?
                * The variable name is misleading - it doesn't mean user deleted text.
                * It means: "Did the save operation change the paragraph structure?"
                * (changedElementsCount < originalElementsCount → structure changed)
            */
            const wasContentStructureChanged =
                changedElementsCount !== originalElementsCount

            const { adaptMutationToQueryResponse } = await import(
                'components/WorkspaceEditor/utils/mutationResponseAdapter'
            )

            // Transform mutation response to query format
            const adaptedResponse = adaptMutationToQueryResponse(
                mutationResponse.data,
                elementId
            )

            // Get currently selected elementId (family) and activeTab from store
            const currentElementId = useElementItemStore.getState()
                .elementItemValue.elementId
            const currentActiveTab = useWorkspace2Store.getState().activeTab

            // Check if response is stale ignoring optimistic callback when elementIds are not consistent
            if (isStaleResponse(adaptedResponse, currentElementId)) {
                cleanupAutoExpand()
                setSaveOperationInProgress(false)
                return
            }

            // Check if activeTab changed during save operation (tab switch race condition)
            if (
                !isActiveTabConsistent(
                    initialActiveTabRef.current,
                    currentActiveTab
                )
            ) {
                cleanupAutoExpand()
                fetchTrackChangesDataWithApolloClient()
                setSaveOperationInProgress(false)
                return // Ignoring callback - tab switched
            }

            // Extract response nodes
            const responseNodes =
                (adaptedResponse as GetProjectProductFamilyViewResponse)
                    .getProjectProductFamilyView?.contentItemViewNodes ||
                (adaptedResponse as GetProjectNuggetContentViewResponse)
                    .getProjectNugget?.contentItemViewNodes ||
                []

            // Strategy: Update or remove node based on response
            const findContentItemViewNode = contentItemViewNodes.find(
                (node) => node.id === responseNodes[0]?.id
            )

            if (findContentItemViewNode && responseNodes.length > 0) {
                // Strategy 1: Node exists with same ID - update it in place
                const updatedNode = {
                    ...findContentItemViewNode,
                    ...responseNodes[0],
                    // Preserve BP flags from existing node if server response doesn't include them
                    isBestPractice:
                        responseNodes[0]?.isBestPractice ??
                        findContentItemViewNode?.isBestPractice,
                    isGlobalBestPractice:
                        responseNodes[0]?.isGlobalBestPractice ??
                        findContentItemViewNode?.isGlobalBestPractice,
                    drawingDesignation:
                        responseNodes[0]?.drawingDesignation ||
                        findContentItemViewNode?.drawingDesignation,
                }
                const updatedContentItemViewNodes = contentItemViewNodes.map(
                    (node) => (node.id === updatedNode.id ? updatedNode : node)
                )
                const firstElement = responseNodes[0]?.elements?.[0]
                const firstContentItemId = firstElement?.id

                // For keyed paragraphs, we need the LAST element (value part), not the first (key part)
                const lastElement =
                    responseNodes[0]?.elements?.[
                        responseNodes[0].elements.length - 1
                    ]
                const lastContentItemId = lastElement?.id

                // For keyed paragraphs, we have multiple elements and need to add after the LAST one
                const hasMultipleElements =
                    (responseNodes[0]?.elements?.length || 0) > 1

                /*
                 * COORDINATION STEP 1: Tell the "create new paragraph" handler which paragraph to create after
                 *
                 * When user presses Enter and the save changes the paragraph structure,
                 * we store the SAVED paragraph's ID so the Enter handler knows where to create the new paragraph.
                 *
                 * EXAMPLE:
                 * 1. User has paragraph with temporary ID "temp-123"
                 * 2. User presses Enter → triggers save
                 * 3. Save completes → paragraph now has real ID "real-456"
                 * 4. We store "real-456" in pendingContentItemIdForAddContent
                 * 5. Enter handler reads this ID and creates new paragraph after "real-456"
                 *
                 * Without this fix: Enter handler would try to create after "temp-123" which doesn't exist anymore!
                 */
                if (isEnterOperation && wasContentStructureChanged) {
                    // For keyed paragraphs (multiple elements), use LAST element to add content after the value part
                    // For regular content, use first element
                    const targetElementId = hasMultipleElements
                        ? lastContentItemId
                        : firstContentItemId
                    setPendingContentItemIdForAddContent(
                        targetElementId || null
                    )
                } else {
                    setPendingContentItemIdForAddContent(null)
                }
                // Auto-expand newly added article if applicable
                if (updatedContentItemViewNodes.length > 0) {
                    handleAutoExpandAfterSave(updatedContentItemViewNodes)
                    updateTreeViewNodes(updatedContentItemViewNodes)
                    setShouldTriggerNewContent?.(isEnterOperation)
                }
            } else {
                // Strategy 2: Node not found or different structure - remove old and add new
                // Find the node containing the textElement.id being updated
                const nodeToRemove = contentItemViewNodes.find((node) =>
                    node.elements?.some((element) =>
                        hasElementWithId(element, textElement.id)
                    )
                )

                const filteredNodes = nodeToRemove
                    ? contentItemViewNodes.filter(
                          (node) => node.id !== nodeToRemove.id
                      )
                    : contentItemViewNodes

                // Preserve BP flags from removed node when adding new nodes from response
                const responseNodesWithBPFlags = responseNodes.map((node) => ({
                    ...node,
                    // Preserve BP flags from removed node if response doesn't include them
                    isBestPractice:
                        node?.isBestPractice ?? nodeToRemove?.isBestPractice,
                    isGlobalBestPractice:
                        node?.isGlobalBestPractice ??
                        nodeToRemove?.isGlobalBestPractice,
                    // Preserve drawingDesignation — content save mutations don't modify it
                    drawingDesignation:
                        node?.drawingDesignation ||
                        nodeToRemove?.drawingDesignation,
                }))

                // Add new nodes (if empty, this effectively removes the old node)
                const updatedNodes = [
                    ...filteredNodes,
                    ...responseNodesWithBPFlags,
                ]
                const firstElement = responseNodes[0]?.elements?.[0]
                const firstContentItemId = firstElement?.id

                // For keyed paragraphs, we need the LAST element (value part), not the first (key part)
                const lastElement =
                    responseNodes[0]?.elements?.[
                        responseNodes[0].elements.length - 1
                    ]
                const lastContentItemId = lastElement?.id

                /*
                 * COORDINATION STEP 2: Same as Step 1, but for newly created paragraphs
                 *
                 * This handles the case where save creates a NEW paragraph (not updating existing).
                 * Same problem: temporary ID → real ID, need to track the real ID.
                 *
                 * EXAMPLE:
                 * 1. User adds new text content (gets temporary ID)
                 * 2. User presses Enter → triggers save
                 * 3. Save creates NEW paragraph with real ID from server
                 * 4. We store the real ID so Enter handler knows which paragraph it just created
                 * 5. Next Enter can create content after the correct paragraph
                 */
                if (isEnterOperation) {
                    // For keyed paragraphs, we have multiple elements and need to add after the LAST one
                    const hasMultipleElements =
                        (responseNodes[0]?.elements?.length || 0) > 1
                    // For keyed paragraphs (multiple elements), use LAST element to add content after the value part
                    // For regular content, use first element
                    const targetElementId = hasMultipleElements
                        ? lastContentItemId
                        : firstContentItemId
                    setPendingContentItemIdForAddContent(
                        targetElementId || null
                    )
                }

                // Auto-expand newly added article if applicable
                if (updatedNodes.length > 0) {
                    handleAutoExpandAfterSave(updatedNodes)
                    updateTreeViewNodes(updatedNodes)
                    setShouldTriggerNewContent?.(isEnterOperation)
                }
            }

            // IMPORTANT: Clear save operation flag BEFORE triggering new content
            // Otherwise canAddContent will reject due to isSaveOperationInProgress
            setSaveOperationInProgress(false)
        },
        [
            contentItemViewNodes,
            updateTreeViewNodes,
            handleAutoExpandAfterSave,
            cleanupAutoExpand,
            fetchTrackChangesDataWithApolloClient,
            setSaveOperationInProgress,
            setShouldTriggerNewContent,
            setPendingContentItemIdForAddContent,
            initialActiveTabRef,
        ]
    )

    return { processUpdateResponse }
}
